/**
 * Module 2: Who Are You When You Speak?
 *
 * In which Philemon gives a flawless lecture in another man's voice, and
 * nobody is moved.
 * Primary character: Philemon | Supporting: Xenia. Theron is not here tonight.
 *
 * REWORKED 2026-10-04 to the six rules Evening 1 set
 * (docs/CONTENT_REVIEW_2026-10-04.md):
 *
 *   1. One idea, shown once. Tonight's is: a borrowed voice can be perfect and
 *      still say nothing of yours. Philemon gives the opening of his
 *      first-morning lecture as Eudoros, hands quite still, and you watch a
 *      moth. Then he answers three small questions as himself, hand back on
 *      his knee, and you forget the moth. No guide draws the conclusion.
 *   2. Something happens tonight: Xenia asks her question over the first cup,
 *      he cannot answer it, and she has him show the lecture instead.
 *   3. Somebody is wrong and stays wrong. Philemon will not call the borrowed
 *      voice a mistake: it got him back on his feet and it does not shake.
 *      Xenia does not argue. He is still trying both voices when the lamp
 *      burns low.
 *   4. The reader's turn: Xenia hands over her question, made smaller. Which of
 *      the three is hardest?
 *   5. One box in the open (the impostor phenomenon, straight after "I felt
 *      like a thief"). The rest waits in the closed notes.
 *   6. One exercise in the scene (the three questions), one to take away (one
 *      sentence of your own), and the Ground Finder tool.
 *
 * The technique: before you speak, three questions. What do I know? What do I
 * believe? Why does this matter to me? Philemon answers the first one badly
 * (as Eudoros) and then better (as himself) before the reader is asked.
 *
 * CONTINUITY this evening must keep (other evenings depend on it):
 *   - It opens on the question Evening 1's preview promised ("he will not
 *     thank me for it"), and on last night: the three years of hiding, the
 *     forty, nineteen, the hands over the jug.
 *   - Eudoros coaxed Philemon back when everyone else had given up, and ended
 *     the two borrowed years with: "That was perfect, Philemon. You have
 *     learnt my voice completely. Now I should like to hear yours." Evening 5
 *     quotes the last sentence on the day Eudoros dies. Keep it word for word.
 *   - Xenia has asked Philemon the three questions before (the Ground Finder
 *     tool has him say she taught them to him).
 *   - Theron is at Piraeus with a cargo. He sends word, and Evening 3 opens
 *     with him: "Two evenings of philosophy. My patience is now spent."
 *
 * The one study here (Clance and Imes 1978; Bravata 2020) was checked on
 * 2026-10-04; see docs/audits/finding-your-voice-2026-10/register-modules-2-3.md.
 * The authenticity gap and the account of gradual silencing are the course's
 * own ideas and are labelled as such in the notes.
 */

export const MODULE_02_CONTENT = [
  // --- The question ---
  {
    type: 'narration',
    text: 'The gate is unlatched again. Theron\'s cushion is empty: a ship came in at Piraeus this afternoon and he has gone down to shout at it. So there are three of you at the low table. Xenia has kept the jug for herself tonight. She fills your cup, then Philemon\'s, and she is looking straight at him.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'A few words for the evening, and tonight they are a question. Philemon. Who are you when you speak?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'A teacher of rhetoric. Thirty years at the Lyceum. You know this.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'That is what you are paid for. I asked who stands up.',
  },
  {
    type: 'narration',
    text: 'Philemon opens his mouth. Closes it. Opens it again.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That is an unfair question.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'I did say you would not thank me. Would it be easier to show us? The opening you give the new students on their first morning. Our guest has never heard it.',
  },
  {
    type: 'narration',
    text: 'He stands, and something happens to him. His shoulders settle. His chin comes up a finger\'s width. He turns to you as though you were a whole bench of new students, and the voice that comes out is lower than the one that poured last night\'s wine, and in no hurry at all.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Why does a man fear to speak?',
  },
  {
    type: 'narration',
    text: 'He leaves the question hanging in the air like smoke. A moth is working at the lamp.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'For the reason the young sailor fears the sea. He has not yet learnt that it will bear him up. So we begin as the sailor begins: by trusting what we cannot yet see.',
  },
  {
    type: 'narration',
    text: 'It is very good. The pause was exactly long enough. His hands, you notice, are perfectly still. You notice the moth as well, and that Xenia is choosing an olive. She catches your eye.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Our guest looks as I did the first time I heard it. Impressed, and thinking about supper. It is beautiful, Philemon. Whose is it?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Eudoros\'s. Every word, and the pause.',
  },

  // --- Eudoros ---
  {
    type: 'heading',
    text: 'Eudoros',
  },
  {
    type: 'narration',
    text: 'He sits, and the shoulders go back to being his own.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You heard about my three years of hiding. One man would not let them become four. My teacher, Eudoros. He coaxed me back when everyone else had quietly given up on me. But when I got to my feet I had nothing of my own to stand up as. So I took him. How he stood, where he paused, how he left a question in the air.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And it worked?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'For two years it worked beautifully. I won debates. People took my arm afterwards and said, "You are a natural." I thanked them in his voice, and every time I felt like a thief.',
  },
  {
    type: 'research',
    title: 'Feeling a fraud has a name',
    source: 'Clance and Imes, Psychotherapy: Theory, Research and Practice, 1978; Bravata and colleagues, Journal of General Internal Medicine, 2020',
    paragraphs: [
      'In 1978 two psychologists, Pauline Rose Clance and Suzanne Imes, described the "impostor phenomenon". They had worked with more than 150 highly successful women who privately believed they were not really bright and had fooled everyone who thought otherwise.',
      'Later studies have found the same feelings in men and women alike. How common it is depends on how you measure it: a 2020 review of 62 studies found estimates from 9 to 82 per cent. Clance and Imes found that more success did not, on its own, quiet the feeling.',
    ],
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Do you know who ended it? He did. He watched me give the finest speech of my life: his stance, his pauses, his voice. Afterwards he took my arm and said: "That was perfect, Philemon. You have learnt my voice completely. Now I should like to hear yours."',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And did you have one to give him?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Not that day.',
  },

  // --- Three small questions ---
  {
    type: 'heading',
    text: 'Three small questions',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'May I ask you three smaller things? About that lecture. It is on being afraid to speak. What do you know about that?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That its causes are three, as Eudoros set them out, and that the first of them is the crowd.',
  },
  {
    type: 'narration',
    text: 'The chin has come up again.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'That is what Eudoros knew. What do you know?',
  },
  {
    type: 'narration',
    text: 'He looks at his cup for a while.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That at nineteen it took my own name from me in front of forty men. That it was in my hands last night, over a jug of wine.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What do you believe about it?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That it does not go. The lecture says the sea will bear you up. I have been in that sea for thirty years and I believe it is cold every single time.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And why does that matter to you?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Because nobody said it to me. For three years I thought I was the only coward in Athens.',
  },
  {
    type: 'narration',
    text: 'None of that was well made. His hand has gone to his knee, where it went last night. You have not looked at the moth once.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You have asked me those before.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Every year. Every year you answer the first one as Eudoros.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Because his answer is better. Mine is a man complaining about cold water. The new students want to hear that the sea will hold them, and his voice tells them so without shaking. It got me back on my feet. I will not call it a mistake.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'I have not asked you to.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You are about to.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'I am about to ask our guest.',
  },
  {
    type: 'yourTurn',
    id: 'm2-hardest-question',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'Who are you when you speak? For you I will make it smaller. Think of the next thing you must say to a room. What you know, what you believe, why it matters to you: which is hardest to answer?',
    options: [
      {
        label: 'What I know. Everyone in the room knows more than I do.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'So did everyone in mine, at nineteen. Leave what you have read to them. Find one thing you saw with your own eyes and begin there: "I was there when." Nobody in any room can correct you on that.',
        },
      },
      {
        label: 'What I believe. I say what I think they want to hear.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'What would you say if you knew nobody would mind? Tonight, alone, finish this: "What I actually think is." You need not say it to anyone yet. Only find out what you have been leaving out.',
        },
      },
      {
        label: 'Why it matters to me. I only speak because I have to.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'Who asked you, and why you and not another? Something made them think of you. If you cannot find it, ask what you would be sorry to see lost if nobody said this at all. Begin with that part.',
        },
      },
      {
        label: 'All three. I put on a voice and get through it.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'So do I, still, and I will not call it a mistake. But hold one sentence back from it. Say that one as you would at your own table, and watch who looks up.',
        },
      },
    ],
  },
  {
    type: 'exercise',
    number: 1,
    title: 'Three small questions',
    instructions: 'Think of something you need to say soon, to one person or to a room. Answer Xenia\'s three questions about it, one plain sentence each. Philemon\'s first answer was his teacher\'s. If yours sounds like someone else\'s, try again.',
    prompts: [
      'What do I know about this? Leave out what you have read and what you are qualified in. What have you seen, done or lived through?',
      'What do I believe about it? Your own view, even if it would not go down well.',
      'Why does this matter to me? Why am I the one saying it?',
      'Read your three sentences aloud. Which one sounds most like you?',
    ],
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'The large question you may leave unanswered tonight. Take it home like a pebble in your sandal.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'It is a very good lecture.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'It is. That has never been the complaint.',
  },
  {
    type: 'narration',
    text: 'The lamp has burnt low. Across the table Philemon\'s lips are moving, and you catch the opening of the lecture: once in the deep, unhurried voice, and then again, quieter, in his own. You cannot tell which he likes better. You are not sure he can.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before the next evening',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'One sentence of your own',
    instructions: 'Choose the next time you will speak in front of anyone: a meeting, a call, a few words at a table. Small is fine.',
    prompts: [
      'First look back. The last time you spoke to a group, whose voice were you using? A mentor\'s, a colleague\'s, a speaker you admire? What did you do that was theirs?',
      'Now answer the three questions for the occasion ahead, one sentence each.',
      'Pick one of those sentences and decide to say it there, in the words you would use at your own table. The rest can be in whatever voice gets you through.',
      'Afterwards, ask "Did I say what I meant?" before you ask "How did I come across?"',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'ground-finder',
    title: 'Ground Finder',
    text: 'Philemon\'s tool asks the three questions and keeps your answers. From its history you can open any saved set and read it over before you speak.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Philemon can only tell you what happened to him. Here is what has been studied since, and two ideas that are only ours.',
    items: [
      {
        title: 'More on feeling a fraud',
        paragraphs: [
          'Clance and Imes met the women they wrote about in therapy and in teaching, and the belief held for all their degrees and honours. Later studies have found the same feelings from teenagers to people late in their careers. The 2020 review was led by Dena Bravata.',
          'The impostor phenomenon is not a recognised disorder. Our own suggestion, for a speaker, is to treat it as a sign that your outward success has run ahead of your inner sense of who you are, and to answer it with self-knowledge instead of one more achievement.',
        ],
        source: 'Clance and Imes, Psychotherapy: Theory, Research and Practice, 1978; Bravata and colleagues, Journal of General Internal Medicine, 2020',
      },
      {
        title: 'Our name for the gap',
        paragraphs: [
          'We call what happened to Philemon the authenticity gap: the distance between who you present to the world and who you feel yourself to be. A small gap is normal. We all adjust how we behave to suit the company. When the gap becomes a chasm, it can bring a particular kind of suffering: you succeed, and the success feeds the mask instead of the person wearing it.',
          'Imitating a confident speaker is a reasonable place to start, so Philemon has half a point. The trouble, as we see it, comes when the imitation becomes a permanent substitute.',
          '"What if they find out I am not as good as they think?" has no satisfying answer. "What do I know, believe and care about?" has one, and it can give you a quiet steadiness that does not depend on anyone\'s praise.',
        ],
      },
      {
        title: 'When there was no single bad day',
        paragraphs: [
          'Some people can trace their speaking anxiety to a single humiliating moment, as Philemon can. Many cannot point to one. For them, we think, the silencing may have happened gradually, through years of messages, often unspoken, about who gets to have a voice.',
          'Gender, culture, class, family and schooling can shape our relationship with our own voice long before we stand in front of an audience. Some people were told outright that they were not the kind of person who speaks up. Others took it in through a thousand small moments of being overlooked, interrupted or dismissed. Looking for the roots of your silence is not self-pity. You need to know what you are working with.',
        ],
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'philemon',
    points: [
      'A borrowed voice can get you back on your feet. It can only say what its owner knew.',
      'Feeling a fraud when you are praised is widely reported, and success alone does not seem to quiet it.',
      'Before you speak, answer three questions in plain words: what do I know, what do I believe, why does this matter to me?',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'xenia',
          text: 'A boy has come up from the harbour with word from Theron. He can guess what I asked, he has had enough philosophy to last him until the next vintage, and tomorrow is his.',
        },
        {
          character: 'philemon',
          text: 'Then tomorrow somebody will be made to stand up and be useful. It is usually me.',
        },
      ],
    },
  },
];

export const MODULE_02_EXERCISES = [
  {
    number: 1,
    title: 'Three small questions',
    description: 'Answer for one thing you need to say: what do I know, what do I believe, why does it matter to me?',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'One sentence of your own',
    description: 'Say one sentence in your own words the next time you speak.',
    xpReward: 20,
  },
];

export const MODULE_02_SUMMARY = {
  title: 'Who Are You When You Speak?',
  keyInsight: 'Know what is yours to say before you stand up.',
  characterQuote: {
    character: 'philemon',
    text: 'He took my arm and said: "That was perfect, Philemon. You have learnt my voice completely. Now I should like to hear yours."',
  },
  takeaways: [
    'A borrowed voice can only say what its owner knew.',
    'Feeling a fraud when you are praised is widely reported, and success alone does not seem to quiet it.',
    'Before you speak: what do I know, what do I believe, why does this matter to me?',
  ],
};
